import type { BasketRepoError } from "@chatfood/dbe-client/errors";
import { useToast } from "@chatfood/design-system";
import { useLocale } from "~/composables/use-locale";
import { useBasketStore } from "~/stores/basket";
import { useModalHubStore } from "~/components/modal-hub";

export type BusinessRuleErrorToastOpts = {
  via: "toast";
};

export type BusinessRuleErrorModalOpts = {
  via: "modal";
};

export type BusinessRuleErrorOpts =
  | BusinessRuleErrorToastOpts
  | BusinessRuleErrorModalOpts;

export const useServiceError = () => {
  const basketStore = useBasketStore();
  const modalHub = useModalHubStore();
  const { t } = useLocale();
  const { toastError } = useToast();

  const snakeToCamelCase = (str: string): string =>
    str.replace(/_([a-z])/g, (_, char: string) => char.toUpperCase());

  const camelToSnakeCase = (str: string): string =>
    str.replace(/([A-Z])/g, (_, char: string) => `_${char.toLowerCase()}`);

  const resolveContext = (
    raw: Record<string, unknown> | null | undefined,
  ): Record<string, string> => {
    if (!raw || typeof raw !== "object") return {};

    const normalized: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(raw)) {
      normalized[snakeToCamelCase(key)] = value;
    }

    const resolved: Record<string, string> = {};

    if (normalized.plu || normalized.itemId || normalized.itemIds) {
      const ids = normalized.itemIds;
      const itemId = String(
        normalized.itemId ?? (Array.isArray(ids) ? ids[0] : ids) ?? "",
      );
      const itemPlu = String(normalized.plu ?? "");
      resolved.itemName =
        basketStore.items.find(
          (item) =>
            item.lineId === itemId ||
            item.itemId === itemId ||
            item.plu === itemPlu,
        )?.name ?? "";
    }

    for (const [key, value] of Object.entries(normalized)) {
      if (key === "plu" || key === "itemId" || key === "itemIds") continue;
      resolved[key] = String(value ?? "");
    }

    return resolved;
  };

  const showAsModal = (message: string) => {
    modalHub.hideLoading();
    modalHub.showWarning({
      title: t("composables.use_service_error.modal_title"),
      message,
      confirmLabel: t(
        "composables.use_service_error.modal_acknowledge_cta_label",
      ),
      onWarningConfirm: () => {
        modalHub.hideWarning();
      },
    });
  };

  const showAsToast = (message: string) => {
    toastError(message);
  };

  const display = (message: string, opts?: BusinessRuleErrorOpts) => {
    if (opts?.via === "toast") {
      showAsToast(message);
    } else {
      showAsModal(message);
    }
  };

  const causeMessage = (error: BasketRepoError<"businessRule">): string =>
    (error.cause instanceof Error ? error.cause.message : null) ??
    error.message;

  const handleBusinessRuleError = (
    error: BasketRepoError<"businessRule">,
    opts?: BusinessRuleErrorOpts,
  ): void => {
    // Without a violation code there's nothing to look up — show the raw
    // backend message rather than attempting a (crashing) key lookup.
    if (!error.meta?.violation) {
      display(causeMessage(error), opts);
      return;
    }

    const code = camelToSnakeCase(error.meta.violation);
    const rawContext = error.meta.context as unknown as Record<string, unknown>;
    const key =
      `composables.use_service_error.business_rule.${code}` as Parameters<
        typeof t
      >[0];
    const translated = t(key, resolveContext(rawContext));

    display(translated !== key ? translated : causeMessage(error), opts);
  };

  const handleValidationRuleError = (
    error: BasketRepoError<"validation">,
    opts?: BusinessRuleErrorOpts,
  ): void => {
    const fields = error.meta ?? {};

    const messages = Object.entries(fields)
      .map(([field, backendMessage]) => {
        const code = camelToSnakeCase(field).replace(/\./g, "_");
        const key =
          `composables.use_service_error.validation.${code}` as Parameters<
            typeof t
          >[0];
        const translated = t(key);
        return translated !== key ? translated : backendMessage;
      })
      .filter(Boolean);

    if (!messages.length) return;

    display(messages.join("\n"), opts);
  };

  return { handleBusinessRuleError, handleValidationRuleError };
};
